import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  CUISINE_OPTIONS,
  DIETARY_TYPES,
  EQUIPMENT_OPTIONS,
} from "../../../../constants";
import Dropdown from "../../../../parts/dropdown";
import Modal from "../../../../parts/modal-new";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";
import Toast from "../../../../parts/toast";
import ToolTip from "../../../../parts/tooltip";
import ExtraPreferences from "../../../athlete-portal/nutrition/components/ExtraPreferences";
import KcalInputs from "../../../athlete-portal/nutrition/components/KcalInputs";
import NutrientsChart from "../../../athlete-portal/nutrition/components/NutrientsChart";
import DataSourceModal from "../../../athlete-portal/nutrition/meal-planner/modals/DataSourceModal";

const DIETARY_I18N = {
  Vegetarian: "vegetarian",
  "Gluten-free": "glutenFree",
  Vegan: "vegan",
};

const ALL_EQUIPMENT = EQUIPMENT_OPTIONS.map((item) => item.equipment);

const DATA_SOURCE_TO_SOURCE_ID = {
  "verified-food": "msb",
  afcd: "afcd",
  usda: "usda",
};

const EFFORT_LEVELS = [1, 3, 5];

const effortLevelToStep = (effortLevel) => {
  const level = Number(effortLevel) || 1;
  if (level <= 2) return 1;
  if (level === 3) return 3;
  return 5;
};

const EXTERNAL_FOOD_LIBRARY_URL =
  "/coaching-portal/master-food-library?section=external";

const EXTERNAL_FAVORITES_CONSTRAINTS = {
  breakfast: { min: 2, max: 3, labelKey: "foodTypes.breakfast" },
  lunchDinner: { min: 3, max: 5, labelKey: "foodTypes.lunchDinner" },
  snack: { min: 2, max: 3, labelKey: "foodTypes.snack" },
};

const normalizeCategoryCounts = (payload) => {
  const source = payload?.data || payload || {};
  return {
    breakfast: Number(source.breakfast) || 0,
    lunchDinner: Number(source.lunchDinner) || 0,
    snack: Number(source.snack) || 0,
    total: Number(source.total) || 0,
  };
};

const fetchExternalRecipeCategoryCounts = async () => {
  try {
    const res = await fetch("/actions/meal-planner/ai/recipe-category-count", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      return { breakfast: 0, lunchDinner: 0, snack: 0, total: 0 };
    }
    const payload = await res.json();
    return normalizeCategoryCounts(payload);
  } catch {
    return { breakfast: 0, lunchDinner: 0, snack: 0, total: 0 };
  }
};

const getExternalFavoritesShortages = (counts) => {
  return Object.entries(EXTERNAL_FAVORITES_CONSTRAINTS).reduce(
    (acc, [key, constraint]) => {
      const current = Number(counts?.[key]) || 0;
      if (current < constraint.min) {
        acc.push({ key, current, ...constraint });
      }
      return acc;
    },
    []
  );
};

const GenerateWithAIPlanModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const [isLoading, setIsLoading] = useState(false);
  const [showLowFavoritesWarning, setShowLowFavoritesWarning] = useState(false);
  const [lowFavoritesWarningMessage, setLowFavoritesWarningMessage] =
    useState("");
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const activeUser = locals?.activeUserToRequest || locals?.activeUser || {};
  const [coachMealPlanDataSources, setCoachMealPlanDataSources] = useState(
    () => activeUser?.mealPlanDataSources || ["usda", "afcd", "verified-food"]
  );
  const coachId = activeUser?._id || null;
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showSuccessToast = (message) => {
    if (!message) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), 5000);
  };

  const initialFoodsCount = Array.isArray(locals?.foodsByCoach)
    ? locals.foodsByCoach.length
    : typeof locals?.customFoodsCount === "number"
      ? locals.customFoodsCount
      : 0;

  const initialMealsCount = Array.isArray(locals?.mealsByCoach)
    ? locals.mealsByCoach.length
    : typeof locals?.customMealsCount === "number"
      ? locals.customMealsCount
      : 0;

  const initialRecipesCount =
    typeof locals?.recipeCount === "number" ? locals.recipeCount : 0;

  const initialFavoritesCount = (() => {
    const list = Array.isArray(locals?.externalFavoriteIds)
      ? locals.externalFavoriteIds
      : Array.isArray(locals?.externalFavoriteIds?.data?.ids)
        ? locals.externalFavoriteIds.data.ids
        : Array.isArray(locals?.externalFavoriteIds?.ids)
          ? locals.externalFavoriteIds.ids
          : null;
    if (list) return list.length;
    if (typeof locals?.favoritedRecipesCount === "number") {
      return locals.favoritedRecipesCount;
    }
    return 0;
  })();

  const [counts, setCounts] = useState({
    cfoods: initialFoodsCount,
    cmeals: initialMealsCount,
    crecipes: initialRecipesCount,
    fav: initialFavoritesCount,
  });

  useEffect(() => {
    fetch("/actions/external-food/favorite-ids", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (!payload) return;
        const ids = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data?.ids)
            ? payload.data.ids
            : Array.isArray(payload?.ids)
              ? payload.ids
              : [];
        setCounts((prev) => ({ ...prev, fav: ids.length }));
      })
      .catch(() => {});
  }, []);

  const [selectedSources, setSelectedSources] = useState({
    cfoods: true,
    cmeals: true,
    crecipes: true,
    fav: true,
    msb: true,
    afcd: true,
    usda: true,
  });

  const toggleSource = (id) => {
    setSelectedSources((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const yoursDef = [
    {
      id: "cfoods",
      name: t("generateAIPlan.customFoods"),
      desc: t("generateAIPlan.customFoodsDesc"),
      count: counts.cfoods,
      unit: t("generateAIPlan.unitFoods"),
      recipe: false,
    },
    {
      id: "cmeals",
      name: t("generateAIPlan.customMeals"),
      desc: t("generateAIPlan.customMealsDesc"),
      count: counts.cmeals,
      unit: t("generateAIPlan.unitMeals"),
      recipe: true,
    },
    {
      id: "crecipes",
      name: t("generateAIPlan.customRecipes"),
      desc: t("generateAIPlan.customRecipesDesc"),
      count: counts.crecipes,
      unit: t("generateAIPlan.unitRecipes"),
      recipe: true,
    },
    {
      id: "fav",
      name: t("generateAIPlan.favoritedRecipes"),
      desc: t("generateAIPlan.favoritedRecipesDesc"),
      count: counts.fav,
      unit: t("generateAIPlan.unitRecipes"),
      recipe: true,
    },
  ];

  const dbDef = [
    {
      id: "msb",
      name: t("generateAIPlan.msbFoodLibrary"),
      desc: t("generateAIPlan.msbFoodLibraryDesc"),
      available: coachMealPlanDataSources.includes("verified-food"),
    },
    {
      id: "afcd",
      name: t("generateAIPlan.afcd"),
      desc: t("generateAIPlan.afcdDesc"),
      available: coachMealPlanDataSources.includes("afcd"),
    },
    {
      id: "usda",
      name: t("generateAIPlan.usda"),
      desc: t("generateAIPlan.usdaDesc"),
      available: coachMealPlanDataSources.includes("usda"),
    },
  ];

  const buildSourceItems = (list) => {
    return list.map((s) => {
      const available = s.available !== false && s.count !== 0;
      const checked = available && Boolean(selectedSources[s.id]);
      const countLabel =
        s.count === 0
          ? t("generateAIPlan.noneYet")
          : s.count != null
            ? `${s.count} ${s.unit}`
            : "";

      return {
        ...s,
        checked,
        disabled: !available,
        unavailable: s.available === false,
        countLabel,
        toggle: () => toggleSource(s.id),
      };
    });
  };

  const yoursSources = buildSourceItems(yoursDef);
  const dbSources = buildSourceItems(dbDef);

  const checkedSources = Object.fromEntries(
    [...yoursSources, ...dbSources].map((s) => [s.id, s.checked])
  );

  const totalRecipes = yoursSources.reduce(
    (sum, s) => sum + (s.recipe && s.checked ? s.count : 0),
    0
  );

  const isForcedPublic = totalRecipes < 50;
  const progressPercent = Math.min(100, Math.round((totalRecipes / 50) * 100));
  const progressBarColor = isForcedPublic ? "#d08a12" : "#1fae7a";

  const [cuisineSearch, setCuisineSearch] = useState("");
  const [showCuisineSuggestions, setShowCuisineSuggestions] = useState(false);
  const cuisineInputRef = useRef(null);
  const cuisineDropdownRef = useRef(null);
  const allergyTextareaRef = useRef(null);
  const dislikedIngredientTextareaRef = useRef(null);

  const [form, setForm] = useState({
    calories: "",
    proteins: "",
    carbs: "",
    fats: "",
    fibers: "",
    proteinsPercent: "",
    carbsPercent: "",
    fatsPercent: "",
    extraPreferences: "",
    selectedPresets: [],
    title: "",
    description: "",
    type: "template",
    effortLevel: 1,
    variance: "medium",
    dietaryRestrictions: {
      allergies: [],
      dietType: "",
    },
    foodPreferences: {
      lovedCuisines: [],
      dislikedIngredients: [],
      relationCuisine: "",
      flavorProfile: [],
    },
    cookingPreferences: {
      prepTime: "",
      equipment: ALL_EQUIPMENT,
    },
  });

  const {
    calories,
    proteins,
    carbs,
    fats,
    fibers,
    proteinsPercent,
    carbsPercent,
    fatsPercent,
    extraPreferences,
    selectedPresets,
    title,
    description,
    type,
    effortLevel,
    variance,
    dietaryRestrictions,
    foodPreferences,
    cookingPreferences,
  } = form;

  const handleChange = (e, updates = null) => {
    e.persist?.();
    const { name, value, type: inputType, checked } = e.target || {};
    const val =
      inputType === "checkbox" ? Boolean(checked) : (value ?? "").toString();

    setForm((prev) => ({ ...prev, ...(updates || { [name]: val }) }));
  };

  const handleAllergyAdd = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const allergy = e.target.value.trim();
      setForm((prev) => {
        if (prev.dietaryRestrictions.allergies.includes(allergy)) {
          return prev;
        }
        return {
          ...prev,
          dietaryRestrictions: {
            ...prev.dietaryRestrictions,
            allergies: [...prev.dietaryRestrictions.allergies, allergy],
          },
        };
      });
      e.target.value = "";
    }
  };

  const handleAllergyRemove = (allergy) => {
    setForm((prev) => ({
      ...prev,
      dietaryRestrictions: {
        ...prev.dietaryRestrictions,
        allergies: prev.dietaryRestrictions.allergies.filter(
          (a) => a !== allergy
        ),
      },
    }));
  };

  const handleDietTypeSelect = (val) => {
    setForm((prev) => ({
      ...prev,
      dietaryRestrictions: {
        ...prev.dietaryRestrictions,
        dietType: val,
      },
    }));
  };

  const filteredCuisines = cuisineSearch
    ? CUISINE_OPTIONS.filter((cuisine) =>
        cuisine._id.toLowerCase().includes(cuisineSearch.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cuisineDropdownRef.current &&
        !cuisineDropdownRef.current.contains(event.target)
      ) {
        setShowCuisineSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCuisineSearchChange = (e) => {
    const value = e.target.value;
    setCuisineSearch(value);
    setShowCuisineSuggestions(value.length > 0 && filteredCuisines.length > 0);
  };

  const handleCuisineSelect = (cuisineName) => {
    setForm((prev) => {
      if (prev.foodPreferences.lovedCuisines.includes(cuisineName)) {
        return prev;
      }
      return {
        ...prev,
        foodPreferences: {
          ...prev.foodPreferences,
          lovedCuisines: [...prev.foodPreferences.lovedCuisines, cuisineName],
        },
      };
    });
    setCuisineSearch("");
    setShowCuisineSuggestions(false);
  };

  const handleCuisineKeyDown = (e) => {
    if (e.key === "Enter" && cuisineSearch.trim()) {
      e.preventDefault();
      let cuisineToAdd = null;

      if (filteredCuisines.length > 0) {
        cuisineToAdd = filteredCuisines[0]._id;
      } else {
        const trimmedSearch = cuisineSearch.trim();
        const matchingCuisine = CUISINE_OPTIONS.find(
          (cuisine) => cuisine._id.toLowerCase() === trimmedSearch.toLowerCase()
        );

        if (matchingCuisine) {
          cuisineToAdd = matchingCuisine._id;
        } else {
          return;
        }
      }

      handleCuisineSelect(cuisineToAdd);
    } else if (e.key === "Escape") {
      setShowCuisineSuggestions(false);
    }
  };

  const handleCuisineRemove = (cuisine) => {
    setForm((prev) => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        lovedCuisines: prev.foodPreferences.lovedCuisines.filter(
          (c) => c !== cuisine
        ),
      },
    }));
  };

  const handleDislikedIngredientAdd = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const ingredient = e.target.value.trim();
      setForm((prev) => {
        if (prev.foodPreferences.dislikedIngredients.includes(ingredient)) {
          return prev;
        }
        return {
          ...prev,
          foodPreferences: {
            ...prev.foodPreferences,
            dislikedIngredients: [
              ...prev.foodPreferences.dislikedIngredients,
              ingredient,
            ],
          },
        };
      });
      e.target.value = "";
    }
  };

  const handleDislikedIngredientRemove = (ingredient) => {
    setForm((prev) => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        dislikedIngredients: prev.foodPreferences.dislikedIngredients.filter(
          (i) => i !== ingredient
        ),
      },
    }));
  };

  const usesExternalRecipes = isForcedPublic || Boolean(checkedSources.fav);

  const runGeneration = () => {
    setShowLowFavoritesWarning(false);
    setLowFavoritesWarningMessage("");
    setIsLoading(true);

    const combinedPreferences = [extraPreferences?.trim(), ...selectedPresets]
      .filter(Boolean)
      .join(". ");

    const excludeSources = [];
    if (!checkedSources.cfoods) excludeSources.push("coachFoods");
    if (!checkedSources.cmeals) excludeSources.push("coachMeals");
    if (!checkedSources.crecipes) excludeSources.push("coachRecipes");
    if (!checkedSources.fav) excludeSources.push("externalRecipes");
    if (!checkedSources.msb) excludeSources.push("verifiedFoods");
    if (!checkedSources.usda) excludeSources.push("usda");
    if (!checkedSources.afcd) excludeSources.push("afcd");

    const body = {
      targetCalories: Number(calories || 0),
      targetMacros: {
        proteinPercentage: Number(proteinsPercent || 0),
        carbsPercentage: Number(carbsPercent || 0),
        fatsPercentage: Number(fatsPercent || 0),
      },
      extraPreferences: combinedPreferences,
      title: title || t("generateAIPlan.defaultPlanTitle"),
      description: description || t("generateAIPlan.defaultPlanDescription"),
      coachId: coachId || undefined,
      type: type || "template",
      effortLevel: Number(effortLevel) || 1,
      usePublicRecipes: Boolean(isForcedPublic),
      selectedSources: {
        customFoods: Boolean(checkedSources.cfoods),
        customMeals: Boolean(checkedSources.cmeals),
        customRecipes: Boolean(checkedSources.crecipes),
        favoritedRecipes: Boolean(checkedSources.fav),
        msbFoodLibrary: Boolean(checkedSources.msb),
        afcd: Boolean(checkedSources.afcd),
        usda: Boolean(checkedSources.usda),
        publicRecipeLibrary: Boolean(isForcedPublic),
      },
      excludeSources,
      variance: variance || "medium",
      dietaryRestrictions: {
        allergies: dietaryRestrictions.allergies || [],
        dietType: dietaryRestrictions.dietType || "Normal",
      },
      foodPreferences: {
        lovedCuisines: foodPreferences.lovedCuisines || [],
        dislikedIngredients: foodPreferences.dislikedIngredients || [],
        relationCuisine: foodPreferences.relationCuisine || "",
        flavorProfile: foodPreferences.flavorProfile || [],
      },
      cookingPreferences: {
        prepTime: Number(cookingPreferences.prepTime) || null,
        equipment:
          cookingPreferences.equipment &&
          cookingPreferences.equipment.length > 0
            ? cookingPreferences.equipment
            : ALL_EQUIPMENT,
      },
    };

    console.log("Full request body:", JSON.stringify(body, null, 2));

    submit(`/actions/meal-planner/generate-meal-plan`, {
      method: "POST",
      body,
    });
  };

  const handleGenerate = async () => {
    const allergyTextarea = allergyTextareaRef.current;
    const dislikedIngredientTextarea = dislikedIngredientTextareaRef.current;
    const hasUncommittedAllergy = allergyTextarea?.value?.trim() || "";
    const hasUncommittedCuisine = cuisineSearch?.trim() || "";
    const hasUncommittedIngredient =
      dislikedIngredientTextarea?.value?.trim() || "";
    if (
      hasUncommittedAllergy ||
      hasUncommittedCuisine ||
      hasUncommittedIngredient
    ) {
      const message = t("generateAIPlan.pressEnterBeforeSubmitting");

      submit("/actions/common/toast", {
        method: "POST",
        body: {
          type: "error",
          message: message,
        },
      });
      setIsLoading(false);
      return;
    }

    if (usesExternalRecipes) {
      const categoryCounts = await fetchExternalRecipeCategoryCounts();
      const shortages = getExternalFavoritesShortages(categoryCounts);

      if (shortages.length > 0) {
        const shortageText = shortages
          .map(({ labelKey, current, min, max }) =>
            t("generateAIPlan.lowExternalFavoritesCategoryShortage", {
              category: t(labelKey),
              current,
              min,
              max,
            })
          )
          .join(" ");

        setLowFavoritesWarningMessage(
          t("generateAIPlan.lowExternalFavoritesMessage", {
            shortages: shortageText,
          })
        );
        setShowLowFavoritesWarning(true);
        return;
      }
    }

    runGeneration();
  };

  const handleGoToExternalFoods = () => {
    window.location.assign(EXTERNAL_FOOD_LIBRARY_URL);
  };

  return (
    <>
      <Modal
        className="generate-with-ai-modal"
        title={t("generateAIPlan.modalTitle")}
      >
        <div className="body generate-meal-plan-body">
          {/* Section 1: Basic Details */}
          <div className="generate-section">
            <div className="section-title-col">
              <h3 className="section-title">
                {t("generateAIPlan.basisDetails")}
              </h3>
            </div>
            <div className="section-content-col">
              <div className="input-group">
                <label htmlFor="generation-plan-title" className="input-label">
                  {t("generateAIPlan.planTitle")}
                </label>
                <input
                  id="generation-plan-title"
                  name="title"
                  type="text"
                  className="text-input"
                  placeholder={t("generateAIPlan.planTitlePlaceholder")}
                  value={title}
                  onChange={handleChange}
                  maxLength={60}
                />
              </div>

              <div className="input-group">
                <label
                  htmlFor="generation-plan-description"
                  className="input-label"
                >
                  {t("generateAIPlan.description")}
                </label>
                <input
                  id="generation-plan-description"
                  name="description"
                  type="text"
                  className="text-input"
                  placeholder={t("generateAIPlan.descriptionPlaceholder")}
                  value={description}
                  onChange={handleChange}
                  maxLength={100}
                />
              </div>

              <div className="sub-grid-2col">
                {/* Variety */}
                <div className="input-group">
                  <span className="input-label">
                    {t("generateAIPlan.variety")}
                  </span>
                  <div className="stacked-radio-card">
                    <label className="stacked-radio-row">
                      <input
                        type="radio"
                        name="variance"
                        value="no variety"
                        checked={variance === "no variety"}
                        onChange={handleChange}
                      />
                      <span>{t("generateAIPlan.noVariety")}</span>
                    </label>
                    <label className="stacked-radio-row">
                      <input
                        type="radio"
                        name="variance"
                        value="medium"
                        checked={variance === "medium"}
                        onChange={handleChange}
                      />
                      <span>{t("generateAIPlan.medium")}</span>
                    </label>
                    <label className="stacked-radio-row">
                      <input
                        type="radio"
                        name="variance"
                        value="max"
                        checked={variance === "max"}
                        onChange={handleChange}
                      />
                      <span>{t("generateAIPlan.max")}</span>
                    </label>
                  </div>
                </div>

                {/* Cooking Effort Level */}
                <div className="input-group">
                  <label
                    htmlFor="generation-effort-level"
                    className="input-label"
                  >
                    {t("generateAIPlan.cookingEffortLevel")}
                  </label>
                  <div className="effort-slider-card">
                    <input
                      id="generation-effort-level"
                      type="range"
                      min="0"
                      max="2"
                      step="1"
                      value={EFFORT_LEVELS.indexOf(
                        effortLevelToStep(effortLevel)
                      )}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "effortLevel",
                            value: EFFORT_LEVELS[Number(e.target.value)],
                          },
                        });
                      }}
                      className="effort-range-slider"
                    />
                    <div className="effort-slider-labels">
                      <span
                        className={
                          effortLevelToStep(effortLevel) === 1 ? "active" : ""
                        }
                      >
                        {t("generateAIPlan.easy")}
                      </span>
                      <span
                        className={
                          effortLevelToStep(effortLevel) === 3 ? "active" : ""
                        }
                      >
                        {t("generateAIPlan.medium")}
                      </span>
                      <span
                        className={
                          effortLevelToStep(effortLevel) === 5 ? "active" : ""
                        }
                      >
                        {t("generateAIPlan.hard")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Calories & Macros */}
          <div className="generate-section">
            <div className="section-title-col">
              <h3 className="section-title">
                {t("generateAIPlan.caloriesAndMacros")}
              </h3>
              <p className="section-subtitle">
                {t("generateAIPlan.caloriesAndMacrosDesc")}
              </p>
            </div>
            <div className="section-content-col">
              <div className="macros-card">
                <div className="macros-card-inputs">
                  <KcalInputs
                    calories={calories}
                    proteins={proteins}
                    proteinsPercent={proteinsPercent}
                    carbs={carbs}
                    carbsPercent={carbsPercent}
                    fats={fats}
                    fibers={fibers}
                    fatsPercent={fatsPercent}
                    handleChange={handleChange}
                  />
                </div>
                <div className="macros-card-chart">
                  <NutrientsChart
                    providedId={"nutrient-chart-generate-with-ai"}
                    dayStats={{ nutrients: { proteins, carbs, fats } }}
                    width={220}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Recipe Sources */}
          <div className="generate-section">
            <div className="section-title-col">
              <h3 className="section-title">
                {t("generateAIPlan.recipeSources")}
              </h3>
              <p className="section-subtitle">
                {t("generateAIPlan.recipeSourcesDesc")}
              </p>
            </div>
            <div className="section-content-col">
              <div className="sources-header-row">
                <p className="sources-intro-note">
                  {t("generateAIPlan.recipeSourcesNote")}
                </p>
                <button
                  type="button"
                  onClick={() => setShowDataSourceModal(true)}
                  className="manage-sources-link"
                >
                  <span>{t("generateAIPlan.manageDataSources")}</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <title>{t("generateAIPlan.manageDataSources")}</title>
                    <path d="M7 17L17 7M9 7h8v8" />
                  </svg>
                </button>
              </div>

              {/* Recipes available progress card */}
              <div className="recipes-progress-card">
                <div className="recipes-progress-header">
                  <span className="recipes-progress-title">
                    {t("generateAIPlan.recipesAvailableTitle")}
                  </span>
                  <span className="recipes-progress-counter">
                    <span className="recipes-progress-count-bold">
                      {totalRecipes}
                    </span>{" "}
                    / 50 minimum
                  </span>
                </div>
                <div className="recipes-progress-track">
                  <div
                    className="recipes-progress-bar"
                    style={{
                      width: `${progressPercent}%`,
                      backgroundColor: progressBarColor,
                    }}
                  />
                </div>
              </div>

              {/* Your Library */}
              <div className="sources-subgroup">
                <span className="sources-subgroup-title">
                  {t("generateAIPlan.yourLibrary")}
                </span>
                <div className="stacked-sources-card">
                  {yoursSources.map((item) => (
                    <label
                      key={item.id}
                      className={`source-item-row ${item.disabled ? "is-disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={item.checked}
                        disabled={item.disabled}
                        onChange={item.toggle}
                        className="source-checkbox"
                      />
                      <span className="source-info">
                        <span
                          className={`source-name ${item.disabled ? "is-muted" : ""}`}
                        >
                          {item.name}
                        </span>
                        <span className="source-desc">{item.desc}</span>
                      </span>
                      {item.countLabel && (
                        <span className="source-badge">{item.countLabel}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Food databases */}
              <div className="sources-subgroup">
                <span className="sources-subgroup-title">
                  {t("generateAIPlan.foodDatabases")}{" "}
                  <span className="sources-subgroup-subtitle">
                    · {t("generateAIPlan.ingredientsOnly")}
                  </span>
                </span>
                <div className="stacked-sources-card">
                  {dbSources.map((item) => (
                    <label
                      key={item.id}
                      className={`source-item-row ${item.disabled ? "is-disabled" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={item.checked}
                        disabled={item.disabled}
                        onChange={item.toggle}
                        className="source-checkbox"
                      />
                      <span className="source-info">
                        <span
                          className={`source-name ${item.disabled ? "is-muted" : ""}`}
                        >
                          {item.name}
                        </span>
                        <span className="source-desc">{item.desc}</span>
                      </span>
                      {item.unavailable && (
                        <button
                          type="button"
                          className="turn-on-sources-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDataSourceModal(true);
                          }}
                        >
                          {t("generateAIPlan.turnOnInDataSources")}
                        </button>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Backup */}
              <div className="sources-subgroup">
                <span className="sources-subgroup-title">
                  {t("generateAIPlan.backup")}
                </span>
                {isForcedPublic ? (
                  <div className="backup-card backup-required">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      aria-label={t("generateAIPlan.publicRecipeLibrary")}
                      className="source-checkbox backup-checkbox-required"
                    />
                    <span className="backup-info">
                      <span className="backup-title">
                        {t("generateAIPlan.publicRecipeLibrary")}
                      </span>
                      <span className="backup-desc">
                        {t("generateAIPlan.publicRecipeRequiredDesc", {
                          total: totalRecipes,
                        })}
                      </span>
                    </span>
                    <span className="backup-badge badge-required">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <title>{t("generateAIPlan.required")}</title>
                        <rect x="5" y="11" width="14" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      {t("generateAIPlan.required")}
                    </span>
                  </div>
                ) : (
                  <div className="backup-card backup-not-needed">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={true}
                      aria-label={t("generateAIPlan.publicRecipeLibrary")}
                      className="source-checkbox"
                    />
                    <span className="backup-info">
                      <span className="backup-title not-needed-title">
                        {t("generateAIPlan.publicRecipeLibrary")}
                      </span>
                      <span className="backup-desc">
                        {t("generateAIPlan.publicRecipeNotNeededDesc")}
                      </span>
                    </span>
                    <span className="backup-badge badge-not-needed">
                      {t("generateAIPlan.notNeeded")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Dietary Restrictions */}
          <div className="generate-section">
            <div className="section-title-col">
              <h3 className="section-title">
                {t("generateAIPlan.dietaryRestrictions")}
              </h3>
            </div>
            <div className="section-content-col">
              <div className="sub-grid-2col">
                {/* Diet Type */}
                <div className="input-group">
                  <span className="input-label">
                    {t("generateAIPlan.dietType")}
                  </span>
                  <div className="stacked-radio-card">
                    <label className="stacked-radio-row">
                      <input
                        type="radio"
                        name="dietType"
                        value=""
                        checked={dietaryRestrictions.dietType === ""}
                        onChange={(e) => handleDietTypeSelect(e.target.value)}
                      />
                      <span>{t("generateAIPlan.none")}</span>
                    </label>
                    {DIETARY_TYPES.slice(0, 3).map((diet) => (
                      <label key={diet.value} className="stacked-radio-row">
                        <input
                          type="radio"
                          name="dietType"
                          value={diet.value}
                          checked={dietaryRestrictions.dietType === diet.value}
                          onChange={(e) => handleDietTypeSelect(e.target.value)}
                        />
                        <span>
                          {t(`dietaryTypes.${DIETARY_I18N[diet.value]}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Allergies */}
                <div className="input-group">
                  <label htmlFor="generation-allergies" className="input-label">
                    {t("generateAIPlan.allergies")}
                  </label>
                  <div
                    className="tags-card-container cursor-text"
                    onClick={() => allergyTextareaRef.current?.focus()}
                  >
                    {dietaryRestrictions.allergies.length > 0 && (
                      <div className="tags-flex-wrap">
                        {dietaryRestrictions.allergies.map((allergy) => (
                          <span key={allergy} className="item-tag">
                            {allergy}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAllergyRemove(allergy);
                              }}
                              className="item-tag-remove-btn"
                              title={t("generateAIPlan.removeAllergy")}
                            >
                              <img
                                className="tag-remove-icon"
                                src="/img/new-icons/red-cross.svg"
                                alt="remove"
                              />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <input
                      ref={allergyTextareaRef}
                      id="generation-allergies"
                      type="text"
                      placeholder={t("generateAIPlan.pressEnterToAdd")}
                      onKeyDown={handleAllergyAdd}
                      className="seamless-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Food Preferences */}
          <div className="generate-section">
            <div className="section-title-col">
              <h3 className="section-title">
                {t("generateAIPlan.foodPreferences")}
              </h3>
            </div>
            <div className="section-content-col">
              <div className="sub-grid-2col">
                {/* Loved Cuisines */}
                <div className="input-group">
                  <label
                    htmlFor="generation-loved-cuisines"
                    className="input-label"
                  >
                    {t("generateAIPlan.lovedCuisines")}
                  </label>
                  <div
                    className="tags-card-container relative cursor-text"
                    ref={cuisineDropdownRef}
                    onClick={() => cuisineInputRef.current?.focus()}
                  >
                    {foodPreferences.lovedCuisines.length > 0 && (
                      <div className="tags-flex-wrap">
                        {foodPreferences.lovedCuisines.map((cuisine) => (
                          <span key={cuisine} className="item-tag">
                            {cuisine}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCuisineRemove(cuisine);
                              }}
                              className="item-tag-remove-btn"
                              title={t("generateAIPlan.removeCuisine")}
                            >
                              <img
                                className="tag-remove-icon"
                                src="/img/new-icons/red-cross.svg"
                                alt="remove"
                              />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <input
                      ref={cuisineInputRef}
                      id="generation-loved-cuisines"
                      type="text"
                      placeholder={t("generateAIPlan.pressEnterToAdd")}
                      value={cuisineSearch}
                      onChange={handleCuisineSearchChange}
                      onKeyDown={handleCuisineKeyDown}
                      className="seamless-input"
                    />
                    {showCuisineSuggestions && (
                      <ul className="cuisine-dropdown-list">
                        {filteredCuisines.slice(0, 8).map((cuisine) => (
                          <li
                            key={cuisine._id}
                            tabIndex={0}
                            role="option"
                            aria-selected={false}
                            onClick={() => handleCuisineSelect(cuisine._id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleCuisineSelect(cuisine._id);
                              }
                            }}
                            className="cuisine-dropdown-item"
                          >
                            {cuisine._id}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Disliked Ingredients */}
                <div className="input-group">
                  <label
                    htmlFor="generation-disliked-ingredients"
                    className="input-label"
                  >
                    {t("generateAIPlan.dislikedIngredients")}
                  </label>
                  <div
                    className="tags-card-container cursor-text"
                    onClick={() =>
                      dislikedIngredientTextareaRef.current?.focus()
                    }
                  >
                    {foodPreferences.dislikedIngredients.length > 0 && (
                      <div className="tags-flex-wrap">
                        {foodPreferences.dislikedIngredients.map(
                          (ingredient) => (
                            <span key={ingredient} className="item-tag">
                              {ingredient}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDislikedIngredientRemove(ingredient);
                                }}
                                className="item-tag-remove-btn"
                                title={t("generateAIPlan.removeIngredient")}
                              >
                                <img
                                  className="tag-remove-icon"
                                  src="/img/new-icons/red-cross.svg"
                                  alt="remove"
                                />
                              </button>
                            </span>
                          )
                        )}
                      </div>
                    )}
                    <input
                      ref={dislikedIngredientTextareaRef}
                      id="generation-disliked-ingredients"
                      type="text"
                      placeholder={t("generateAIPlan.pressEnterToAdd")}
                      onKeyDown={handleDislikedIngredientAdd}
                      className="seamless-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer>
          <a
            data-button-type="secondary"
            data-action="cancel"
            href="/actions/refresh?modal=&sourceOwner=&week="
          >
            {t("generateAIPlan.cancel")}
          </a>
          <button
            type="button"
            data-button-type="primary"
            onClick={handleGenerate}
            disabled={isLoading}
          >
            {isLoading
              ? t("generateAIPlan.generating")
              : t("generateAIPlan.generateWithAI")}
          </button>
        </footer>
      </Modal>

      {showDataSourceModal && (
        <DataSourceModal
          locals={locals}
          initialSources={coachMealPlanDataSources}
          onClose={() => setShowDataSourceModal(false)}
          onSaved={(updatedSources, message) => {
            setSelectedSources((prev) => {
              const next = { ...prev };
              Object.entries(DATA_SOURCE_TO_SOURCE_ID).forEach(
                ([dataSource, id]) => {
                  const wasOn = coachMealPlanDataSources.includes(dataSource);
                  const isOn = updatedSources.includes(dataSource);
                  if (wasOn !== isOn) next[id] = isOn;
                }
              );
              return next;
            });
            setCoachMealPlanDataSources(updatedSources);
            setShowDataSourceModal(false);
            showSuccessToast(message);
          }}
        />
      )}

      <Toast open={Boolean(toast)} type="success" message={toast ?? ""} />

      <ConfirmationModal
        className="external-favorites-warning-modal"
        isOpen={showLowFavoritesWarning}
        title={t("generateAIPlan.lowExternalFavoritesTitle")}
        contentText={
          lowFavoritesWarningMessage ||
          t("generateAIPlan.lowExternalFavoritesMessage")
        }
        cancelButtonText={t("generateAIPlan.goToExternalFoods")}
        cancelButtonAction={handleGoToExternalFoods}
        confirmButtonText={t("generateAIPlan.runGenerationAnyway")}
        confirmButtonAction={runGeneration}
        confirmSuccess
      />
    </>
  );
};

GenerateWithAIPlanModal.propTypes = {
  locals: PropTypes.object,
};

export default GenerateWithAIPlanModal;
